import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { AttendancePage } from './pages/AttendancePage';
import { PayrollPage } from './pages/PayrollPage';
import { PayrollDetailPage } from './pages/PayrollDetailPage';
import { BonusesPage } from './pages/BonusesPage';
import { ReportsPage } from './pages/ReportsPage';
import { SettingsPage } from './pages/SettingsPage';
import { LegalPage } from './pages/LegalPage';
import { EmployeePortalPage } from './pages/EmployeePortalPage';
import { JustificationsPage } from './pages/JustificationsPage';
import { LiquidationPage } from './pages/LiquidationPage';
import './index.css';

function Spinner() {
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-on-surface-variant text-sm font-medium">Cargando...</p>
      </div>
    </div>
  );
}

// Pantalla cuando el usuario existe en Auth pero no tiene perfil en la BD
function NoProfilePage() {
  const { signOut } = useAuth();
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <div className="card p-10 max-w-md text-center space-y-4">
        <span className="material-symbols-outlined text-4xl text-error">person_off</span>
        <h2 className="text-xl font-bold font-headline text-on-surface">
          Perfil no configurado
        </h2>
        <p className="text-sm text-on-surface-variant">
          Tu cuenta existe pero no tiene un perfil asignado en el sistema.
          Pide al administrador que ejecute el SQL para crear tu perfil.
        </p>
        <button
          onClick={() => signOut()}
          className="btn-primary text-white px-6 py-2.5 rounded-lg font-bold text-sm w-full"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

// Ruta protegida
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading, noProfile } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  // noProfile es true solo cuando loadUserData terminó y confirmed null
  // No hay estado intermedio posible (estado unificado)
  if (noProfile) return <NoProfilePage />;
  if (!profile) return <Spinner />;
  return <>{children}</>;
}

// Solo admin/hr
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role === 'employee') return <Navigate to="/mi-asistencia" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Redirección basada en rol — se ejecuta solo cuando profile ya está cargado
function RoleRedirect() {
  const { profile, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role === 'employee') return <Navigate to="/mi-asistencia" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<RoleRedirect />} />

        {/* Portal empleado */}
        <Route path="mi-asistencia" element={<EmployeePortalPage />} />

        {/* Admin / HR */}
        <Route path="dashboard"      element={<AdminRoute><DashboardPage /></AdminRoute>} />
        <Route path="employees"      element={<AdminRoute><EmployeesPage /></AdminRoute>} />
        <Route path="attendance"     element={<AdminRoute><AttendancePage /></AdminRoute>} />
        <Route path="payroll"        element={<AdminRoute><PayrollPage /></AdminRoute>} />
        <Route path="payroll/:id"    element={<AdminRoute><PayrollDetailPage /></AdminRoute>} />
        <Route path="bonuses"        element={<AdminRoute><BonusesPage /></AdminRoute>} />
        <Route path="reports"        element={<AdminRoute><ReportsPage /></AdminRoute>} />
        <Route path="legal"          element={<AdminRoute><LegalPage /></AdminRoute>} />
        <Route path="settings"       element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="justifications" element={<AdminRoute><JustificationsPage /></AdminRoute>} />
        <Route path="liquidation"    element={<AdminRoute><LiquidationPage /></AdminRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
